package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.ui.toAttrs
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.ClubListPage
import com.arenaproplay.admin.domain.admin.ClubRow
import com.arenaproplay.admin.domain.admin.CreateClubRequest
import com.arenaproplay.admin.domain.admin.CreateClubServiceRequest
import com.arenaproplay.admin.domain.admin.SportOption
import com.arenaproplay.admin.domain.admin.WorkingHoursEntry
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.forms.AddressAutocompleteField
import com.arenaproplay.admin.ui.components.forms.AmenityChips
import com.arenaproplay.admin.ui.components.forms.CoordinatesHint
import com.arenaproplay.admin.ui.components.forms.FormButton
import com.arenaproplay.admin.ui.components.forms.OTHER_SPORT
import com.arenaproplay.admin.ui.components.forms.SportTypeSelect
import com.arenaproplay.admin.ui.components.forms.TextArea
import com.arenaproplay.admin.ui.components.forms.TextInput
import com.arenaproplay.admin.ui.components.forms.WorkingHoursEditor
import com.arenaproplay.admin.ui.components.forms.defaultWeekHours
import com.arenaproplay.admin.ui.components.forms.parseRatingInput
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.components.overlay.Modal
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.pickImageFile
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img

private fun clubInitials(name: String): String {
    val words = name.trim().split(" ").filter { it.isNotEmpty() }
    return when {
        words.size >= 2 -> "${words[0].first().uppercaseChar()}${words[1].first().uppercaseChar()}"
        words.size == 1 -> words[0].first().uppercaseChar().toString()
        else -> "?"
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// sportPillTone is defined in Pill.kt as a top-level shared utility.

@Page("/clubs")
@Composable
fun ClubsPage() {
    val strings = LocalAppStrings.current
    val repo = LocalAppContainer.current.adminRepository ?: return

    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    var page by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(0) ?: 0) }
    var query by remember { mutableStateOf(urlParam("q") ?: "") }
    var pageSize by remember {
        mutableStateOf(urlParam("size")?.toIntOrNull()?.takeIf { it in PAGE_SIZE_OPTIONS } ?: 25)
    }
    var data by remember { mutableStateOf<ClubListPage?>(null) }
    var reloadToken by remember { mutableStateOf(0) }
    var exporting by remember { mutableStateOf(false) }
    val loadState = rememberLoadState()

    var showCreate by remember { mutableStateOf(false) }
    var sports by remember { mutableStateOf<List<SportOption>>(emptyList()) }

    // Client-side search: when query is non-blank we fetch up to 200 rows (backend max) and
    // filter/paginate locally. Backend has no search param on the clubs listing endpoint;
    // server-side search is a follow-up task.
    var allSearchRows by remember { mutableStateOf<List<ClubRow>?>(null) }
    var searchTotal by remember { mutableStateOf(0L) }  // total from the 200-row fetch

    // Sync URL params whenever page/query/pageSize change
    LaunchedEffect(page, query, pageSize) {
        setUrlParams(
            "page" to page.takeIf { it > 0 }?.toString(),
            "q" to query,
            "size" to pageSize.takeIf { it != 25 }?.toString(),
        )
    }

    LaunchedEffect(page, pageSize, reloadToken, loadState.reloadToken) {
        if (query.isNotBlank()) return@LaunchedEffect   // client-side path handles this
        data = null
        loadState.load {
            data = repo.getClubs(page, pageSize)
        }
    }

    // Client-side search path: fires whenever query changes (or reload is requested)
    LaunchedEffect(query, reloadToken, loadState.reloadToken) {
        if (query.isBlank()) {
            allSearchRows = null
            return@LaunchedEffect
        }
        allSearchRows = null
        data = null
        loadState.load {
            val fetched = repo.getClubs(0, 200)
            searchTotal = fetched.total
            val q = query.trim().lowercase()
            allSearchRows = fetched.clubs.filter { row ->
                row.name.lowercase().contains(q) || row.address.lowercase().contains(q)
            }
        }
    }

    LaunchedEffect(Unit) {
        // Best effort: the create form falls back to "Other" if sports fail to load.
        runCatching { sports = repo.getSports() }
    }

    AdminLayout(
        title = "Клубы",
        subtitle = "Обзор клубов платформы и создание новых",
        actions = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                ExportCsvButton(exporting = exporting) {
                    exporting = true
                    scope.launch {
                        try {
                            // ClubListPage doesn't implement PageResult, so we page manually.
                            val all = mutableListOf<ClubRow>()
                            val exportPageSize = 200
                            var p = 0
                            while (all.size < 10_000) {
                                val result = repo.getClubs(p, exportPageSize)
                                all += result.clubs
                                val loaded = (p + 1) * exportPageSize
                                if (result.clubs.isEmpty() || loaded >= result.total) break
                                p++
                            }
                            downloadCsv(
                                filename = "clubs.csv",
                                header = listOf(
                                    "ID", "Название", "Адрес", "Вид спорта", "Телефон",
                                    "Рейтинг", "Отзывов", "Активен", "Создан",
                                ),
                                rows = all.map {
                                    listOf(
                                        it.id,
                                        it.name,
                                        it.address,
                                        it.customSportName ?: it.type,
                                        it.phone ?: "",
                                        if (it.totalRatingsCount > 0) it.averageRating.asFixed(1) else "",
                                        it.totalRatingsCount.toString(),
                                        if (it.isActive) "Да" else "Нет",
                                        it.createdAt,
                                    )
                                },
                            )
                        } finally {
                            exporting = false
                        }
                    }
                }
                HSpace(8)
                SearchInput(
                    value = query,
                    onValueChange = { query = it; page = 0 },
                    placeholderText = "Поиск клубов…",
                )
                HSpace(8)
                CreateClubButton { showCreate = true }
            }
        }
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        // Result count sub-header
        val isSearchActive = query.isNotBlank()
        val filteredRows = allSearchRows
        // When searching: paginate the locally-filtered list; otherwise use server page.
        val displayedItems: List<ClubRow>
        val displayTotal: Int
        if (isSearchActive) {
            val all = filteredRows.orEmpty()
            displayTotal = all.size
            displayedItems = all.drop(page * pageSize).take(pageSize)
        } else {
            displayTotal = data?.total?.toInt() ?: 0
            displayedItems = data?.clubs.orEmpty()
        }
        val isLoading = if (isSearchActive) filteredRows == null else data == null

        if (!isLoading) {
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (isSearchActive) {
                        val suffix = if (searchTotal > 200) " (по первым 200)" else ""
                        "Найдено: $displayTotal клубов$suffix"
                    } else {
                        "Всего: $displayTotal клубов"
                    },
                    Modifier.bodySMedium().color(AppColors.Muted)
                )
            }
            VSpace(10)
        }

        // DataTable v2 inside SectionCard
        SectionCard(modifier = Modifier.fillMaxWidth()) {
            DataTable(
                items = displayedItems,
                loading = isLoading,
                onRowClick = { ctx.router.navigateTo(Routes.clubDetails(it.id)) },
                columns = listOf(
                    // Column 1: avatar initials + name + address
                    TableColumn<ClubRow>(
                        header = "КЛУБ",
                        weight = 2f,
                    ) { row ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            AvatarBubble(seed = row.id, initials = clubInitials(row.name), sizePx = 34)
                            HSpace(10)
                            Column {
                                SpanText(row.name, Modifier.bodyMMedium().color(AppColors.Ink))
                                Box(Modifier.height(2.px))
                                SpanText(
                                    row.address.ifBlank { "—" },
                                    Modifier.bodySMedium().color(AppColors.Muted)
                                        .styleModifier { property("font-size", "11px") }
                                )
                            }
                        }
                    },

                    // Column 2: sport type Pill
                    TableColumn<ClubRow>(
                        header = "ВИД СПОРТА",
                        weight = 1.2f,
                    ) { row ->
                        val label = row.customSportName ?: row.type.lowercase()
                            .replaceFirstChar { it.uppercase() }
                        Pill(text = label, tone = sportPillTone(row.type))
                    },

                    // Column 3: phone
                    TableColumn<ClubRow>(
                        header = "ТЕЛЕФОН",
                        weight = 1f,
                    ) { row ->
                        SpanText(
                            row.phone ?: "—",
                            Modifier.bodyMRegular().color(AppColors.Muted),
                        )
                    },

                    // Column 4: rating
                    TableColumn<ClubRow>(
                        header = "РЕЙТИНГ",
                        weight = 0.9f,
                    ) { row ->
                        if (row.totalRatingsCount > 0) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                LucideIcon(name = "star", size = 14, color = AppColors.ProGold.toRgbString())
                                HSpace(5)
                                SpanText(
                                    "${row.averageRating.asFixed(1)} (${row.totalRatingsCount})",
                                    Modifier.bodyMRegular().color(AppColors.Muted),
                                )
                            }
                        } else {
                            SpanText("—", Modifier.bodyMRegular().color(AppColors.Faint))
                        }
                    },

                    // Column 5: created date
                    TableColumn<ClubRow>(
                        header = "СОЗДАН",
                        weight = 1f,
                    ) { row ->
                        SpanText(
                            formatTimestampShort(row.createdAt),
                            Modifier.bodyMRegular().color(AppColors.Muted)
                                .styleModifier { property("font-size", "11px") }
                        )
                    },

                    // Column 6: status Pill
                    TableColumn<ClubRow>(
                        header = "СТАТУС",
                        weight = 0.8f,
                    ) { row ->
                        if (row.isActive) Pill("Активен", PillTone.Success)
                        else Pill("Неактивен", PillTone.Neutral)
                    },
                ),
                emptyText = strings.emptyData,
            )
        }

        VSpace(12)

        PaginationBar(
            page = page,
            pageSize = pageSize,
            total = displayTotal,
            onPrev = { if (page > 0) page-- },
            onNext = {
                if ((page + 1) * pageSize < displayTotal) page++
            },
            onPageSizeChange = { pageSize = it; page = 0 }
        )

        VSpace(28)
    }

    CreateClubModal(
        repo = repo,
        isOpen = showCreate,
        sports = sports,
        onClose = { showCreate = false },
        onCreated = {
            showCreate = false
            page = 0
            reloadToken++
        },
    )
}

private data class PendingService(val name: String, val price: String, val description: String?)

@Composable
private fun CreateClubModal(
    repo: AdminRepository,
    isOpen: Boolean,
    sports: List<SportOption>,
    onClose: () -> Unit,
    onCreated: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var name by remember(isOpen) { mutableStateOf("") }
    var address by remember(isOpen) { mutableStateOf("") }
    var latitude by remember(isOpen) { mutableStateOf<Double?>(null) }
    var longitude by remember(isOpen) { mutableStateOf<Double?>(null) }
    var sportType by remember(isOpen) { mutableStateOf("") }
    var customSportName by remember(isOpen) { mutableStateOf("") }
    var phone by remember(isOpen) { mutableStateOf("") }
    var description by remember(isOpen) { mutableStateOf("") }
    var ratingText by remember(isOpen) { mutableStateOf("") }
    var reviewsText by remember(isOpen) { mutableStateOf("") }
    var instagram by remember(isOpen) { mutableStateOf("") }
    var website by remember(isOpen) { mutableStateOf("") }
    var googleUrl by remember(isOpen) { mutableStateOf("") }
    var googleSearch by remember(isOpen) { mutableStateOf("") }
    var week by remember(isOpen) { mutableStateOf(defaultWeekHours()) }
    var amenities by remember(isOpen) { mutableStateOf(setOf<String>()) }
    var services by remember(isOpen) { mutableStateOf(listOf<PendingService>()) }
    var serviceName by remember(isOpen) { mutableStateOf("") }
    var servicePrice by remember(isOpen) { mutableStateOf("") }
    var mainPhotoUrl by remember(isOpen) { mutableStateOf<String?>(null) }
    var mainPhotoUrlInput by remember(isOpen) { mutableStateOf("") }
    var uploadingMain by remember(isOpen) { mutableStateOf(false) }
    var galleryUrls by remember(isOpen) { mutableStateOf(listOf<String>()) }
    var galleryUrlInput by remember(isOpen) { mutableStateOf("") }
    var uploadingGallery by remember(isOpen) { mutableStateOf(false) }
    var creating by remember(isOpen) { mutableStateOf(false) }
    var createdWithWarnings by remember(isOpen) { mutableStateOf(false) }
    var error by remember(isOpen) { mutableStateOf<String?>(null) }

    Modal(
        isOpen = isOpen,
        onClose = { if (!creating) onClose() },
        title = "Создать клуб",
        maxWidth = 640.px,
        closeOnOverlayClick = false,
    ) {
        Column(
            Modifier.fillMaxWidth().styleModifier {
                property("max-height", "70vh")
                property("overflow-y", "auto")
                property("padding-right", "6px")
            }
        ) {
            TextInput(name, { name = it }, label = "Название *", placeholder = "Название клуба")
            Box(Modifier.height(14.px))
            AddressAutocompleteField(
                value = address,
                onValueChange = { address = it; latitude = null; longitude = null },
                onResolved = { address = it.address; latitude = it.latitude; longitude = it.longitude },
                autocomplete = { q, s -> repo.autocompleteAddress(q, s) },
                resolvePlace = { p, s -> repo.getPlaceAddress(p, s) },
                label = "Address *",
                latitude = latitude,
                longitude = longitude,
            )
            CoordinatesHint(latitude, longitude)
            Box(Modifier.height(14.px))
            SportTypeSelect(sports, sportType, label = "Вид спорта *") {
                sportType = it
                if (it != OTHER_SPORT) customSportName = ""
            }
            if (sportType == OTHER_SPORT) {
                Box(Modifier.height(14.px))
                TextInput(
                    customSportName, { customSportName = it },
                    label = "Вид спорта *", placeholder = "например, Падел",
                )
            }

            FormSectionTitle("Главное фото *")
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier.width(150.px).height(100.px).sBorderRadius(8)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("background", AppColors.Background.toRgbString())
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    val url = mainPhotoUrl
                    if (url != null) {
                        Img(
                            src = url,
                            attrs = Modifier.fillMaxSize()
                                .styleModifier { property("object-fit", "cover") }
                                .toAttrs(),
                        )
                    } else {
                        LucideIcon(name = "image", size = 26, color = AppColors.Faint.toRgbString())
                    }
                }
                Box(Modifier.width(14.px))
                FormButton(
                    if (uploadingMain) "Загрузка…" else "Загрузить фото",
                    enabled = !uploadingMain,
                    primary = false,
                ) {
                    pickImageFile { bytes, fileName, contentType ->
                        uploadingMain = true
                        error = null
                        scope.launch {
                            try {
                                mainPhotoUrl = repo.uploadClubImage(bytes, fileName, contentType)
                            } catch (e: Throwable) {
                                error = e.message ?: "Не удалось загрузить фото"
                            } finally {
                                uploadingMain = false
                            }
                        }
                    }
                }
            }
            Box(Modifier.height(10.px))
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f)) {
                    TextInput(
                        mainPhotoUrlInput, { mainPhotoUrlInput = it },
                        placeholder = "Или вставьте ссылку на изображение (https://…)",
                    )
                }
                Box(Modifier.width(10.px))
                FormButton("По ссылке", enabled = true, primary = false) {
                    val url = mainPhotoUrlInput.trim()
                    if (url.startsWith("http://") || url.startsWith("https://")) {
                        mainPhotoUrl = url
                        mainPhotoUrlInput = ""
                        error = null
                    } else {
                        error = "Введите прямую ссылку на изображение, начинающуюся с http(s)://"
                    }
                }
            }

            Box(Modifier.height(14.px))
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(phone, { phone = it }, label = "Телефон", placeholder = "+380…")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(ratingText, { ratingText = it }, label = "Рейтинг", placeholder = "например, 4.5")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(reviewsText, { reviewsText = it }, label = "Отзывов", placeholder = "например, 128")
                }
            }
            Box(Modifier.height(14.px))
            TextInput(instagram, { instagram = it }, label = "Instagram", placeholder = "https://instagram.com/…")  // бренд-название, не переводим
            Box(Modifier.height(14.px))
            Row(Modifier.fillMaxWidth()) {
                Box(Modifier.weight(1f)) {
                    TextInput(website, { website = it }, label = "Сайт", placeholder = "https://…")
                }
                Box(Modifier.width(12.px))
                Box(Modifier.weight(1f)) {
                    TextInput(googleSearch, { googleSearch = it }, label = "Google Search", placeholder = "https://www.google.com/search?q=…")
                }
                Box(Modifier.width(16.px))
                Box(Modifier.weight(1f)) {
                    TextInput(googleUrl, { googleUrl = it }, label = "Google Maps", placeholder = "https://maps.google.com/…")
                }
            }
            Box(Modifier.height(14.px))
            TextArea(
                description, { description = it },
                label = "Описание", placeholder = "Краткое описание (необязательно)", rows = 3,
            )

            FormSectionTitle("Часы работы")
            WorkingHoursEditor(week) { week = it }

            FormSectionTitle("Удобства")
            AmenityChips(amenities, enabled = !creating) { type ->
                amenities = if (type in amenities) amenities - type else amenities + type
            }

            FormSectionTitle("Сервисы")
            services.forEachIndexed { index, service ->
                Row(
                    Modifier.fillMaxWidth().padding(topBottom = 5.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(Modifier.weight(1f)) {
                        SpanText(service.name, Modifier.bodyMRegular().color(AppColors.Ink))
                    }
                    SpanText(service.price, Modifier.bodyMRegular().color(AppColors.Muted))
                    Box(Modifier.width(10.px))
                    Box(
                        Modifier.cursor(Cursor.Pointer).padding(4.px)
                            .onClick { services = services.filterIndexed { i, _ -> i != index } }
                    ) {
                        LucideIcon(name = "x", size = 14, color = AppColors.Error.toRgbString())
                    }
                }
            }
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1.6f)) {
                    TextInput(serviceName, { serviceName = it }, placeholder = "Название сервиса")
                }
                Box(Modifier.width(10.px))
                Box(Modifier.weight(0.8f)) {
                    TextInput(servicePrice, { servicePrice = it }, placeholder = "Цена")
                }
                Box(Modifier.width(10.px))
                FormButton("Добавить", enabled = true, primary = false) {
                    if (serviceName.isNotBlank() && servicePrice.isNotBlank()) {
                        services = services + PendingService(serviceName.trim(), servicePrice.trim(), null)
                        serviceName = ""
                        servicePrice = ""
                    }
                }
            }

            FormSectionTitle("Галерея")
            Row(
                Modifier.fillMaxWidth().styleModifier {
                    property("flex-wrap", "wrap")
                    property("gap", "10px")
                }
            ) {
                galleryUrls.forEachIndexed { index, url ->
                    Box(
                        Modifier.width(110.px).height(74.px).sBorderRadius(8)
                            .styleModifier {
                                property("position", "relative")
                                property("overflow", "hidden")
                                property("background", AppColors.Background.toRgbString())
                            },
                    ) {
                        Img(
                            src = url,
                            attrs = Modifier.fillMaxSize()
                                .styleModifier { property("object-fit", "cover") }
                                .toAttrs(),
                        )
                        Box(
                            Modifier
                                .styleModifier {
                                    property("position", "absolute")
                                    property("top", "4px")
                                    property("right", "4px")
                                    property("background", AppColors.Ink.toRgbaString(0.65f))
                                }
                                .sBorderRadius(999)
                                .padding(4.px)
                                .cursor(Cursor.Pointer)
                                .onClick { galleryUrls = galleryUrls.filterIndexed { i, _ -> i != index } },
                            contentAlignment = Alignment.Center,
                        ) {
                            LucideIcon(name = "x", size = 10, color = AppColors.Surface.toRgbString())
                        }
                    }
                }
                Box(
                    Modifier.width(110.px).height(74.px).sBorderRadius(8)
                        .cursor(if (uploadingGallery) Cursor.NotAllowed else Cursor.Pointer)
                        .styleModifier {
                            property("border", "1.5px dashed ${AppColors.Ink.toRgbaString(0.18f)}")
                            property("background", AppColors.Background.toRgbString())
                        }
                        .onClick {
                            if (uploadingGallery) return@onClick
                            pickImageFile { bytes, fileName, contentType ->
                                uploadingGallery = true
                                error = null
                                scope.launch {
                                    try {
                                        galleryUrls = galleryUrls + repo.uploadClubImage(bytes, fileName, contentType)
                                    } catch (e: Throwable) {
                                        error = e.message ?: "Не удалось загрузить фото"
                                    } finally {
                                        uploadingGallery = false
                                    }
                                }
                            }
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        if (uploadingGallery) "Загрузка…" else "+ Загрузить",
                        Modifier.bodySMedium().color(AppColors.Muted),
                    )
                }
            }
            Box(Modifier.height(10.px))
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.weight(1f)) {
                    TextInput(galleryUrlInput, { galleryUrlInput = it }, placeholder = "Или вставьте ссылку на изображение (https://…)")
                }
                Box(Modifier.width(10.px))
                FormButton("По ссылке", enabled = true, primary = false) {
                    val url = galleryUrlInput.trim()
                    if (url.startsWith("http://") || url.startsWith("https://")) {
                        galleryUrls = galleryUrls + url
                        galleryUrlInput = ""
                        error = null
                    } else {
                        error = "Введите прямую ссылку на изображение, начинающуюся с http(s)://"
                    }
                }
            }

            if (error != null) {
                Box(Modifier.height(12.px))
                SpanText(error!!, Modifier.bodySMedium().color(AppColors.Error))
            }
        }
        Box(Modifier.height(20.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f))
            if (createdWithWarnings) {
                FormButton("Закрыть", enabled = true, primary = true) { onCreated() }
            } else {
                FormButton("Отмена", enabled = !creating, primary = false) { onClose() }
                Box(Modifier.width(10.px))
                FormButton(
                    if (creating) "Создание…" else "Создать",
                    enabled = !creating,
                    primary = true,
                ) {
                    if (name.isBlank()) {
                        error = "Введите название клуба"
                        return@FormButton
                    }
                    if (address.isBlank()) {
                        error = "Введите адрес"
                        return@FormButton
                    }
                    if (sportType.isBlank()) {
                        error = "Выберите вид спорта"
                        return@FormButton
                    }
                    if (sportType == OTHER_SPORT && customSportName.isBlank()) {
                        error = "Для типа «Другой» укажите название вида спорта"
                        return@FormButton
                    }
                    if (mainPhotoUrl == null) {
                        error = "Главное фото обязательно — загрузите или вставьте ссылку"
                        return@FormButton
                    }
                    val rating = ratingText.trim().takeIf { it.isNotBlank() }?.let {
                        val r = parseRatingInput(it)
                        if (r == null || r < 0.0 || r > 5.0) {
                            error = "Рейтинг должен быть числом от 0 до 5, например 4.5"
                            return@FormButton
                        }
                        r
                    }
                    val reviews = reviewsText.trim().takeIf { it.isNotBlank() }?.let {
                        val n = it.toIntOrNull()
                        if (n == null || n < 0) {
                            error = "Количество отзывов должно быть целым числом"
                            return@FormButton
                        }
                        n
                    }
                    creating = true
                    error = null
                    scope.launch {
                        val club = try {
                            repo.createClub(
                                CreateClubRequest(
                                    name = name.trim(),
                                    address = address.trim(),
                                    latitude = latitude,
                                    longitude = longitude,
                                    mainPhotoUrl = mainPhotoUrl,
                                    type = sportType.ifBlank { null },
                                    customSportName = customSportName.trim()
                                        .takeIf { sportType == OTHER_SPORT && it.isNotBlank() },
                                    phone = phone.trim().ifBlank { null },
                                    description = description.trim().ifBlank { null },
                                    instagramUrl = instagram.trim().ifBlank { null },
                                    websiteUrl = website.trim().ifBlank { null },
                                    googleSearchUrl = googleSearch.trim().ifBlank { null },
                                    googleMapsUrl = googleUrl.trim().ifBlank { null },
                                    averageRating = rating,
                                    totalRatingsCount = reviews,
                                )
                            )
                        } catch (e: Throwable) {
                            error = e.message ?: "Не удалось создать клуб"
                            creating = false
                            return@launch
                        }

                        // The club exists now; save the extras and report anything that failed.
                        val failed = mutableListOf<String>()
                        runCatching {
                            repo.updateClubWorkingHours(
                                club.id,
                                week.map { WorkingHoursEntry(it.dayOfWeek, it.open, it.close, it.closed) },
                            )
                        }.onFailure { failed += "working hours" }
                        if (galleryUrls.isNotEmpty()) {
                            runCatching { repo.updateClubGallery(club.id, galleryUrls) }
                                .onFailure { failed += "gallery" }
                        }
                        services.forEach { s ->
                            runCatching {
                                repo.createClubService(
                                    club.id,
                                    CreateClubServiceRequest(s.name, s.description, s.price),
                                )
                            }.onFailure { if ("services" !in failed) failed += "services" }
                        }
                        if (amenities.isNotEmpty()) {
                            runCatching { repo.updateClubAmenities(club.id, amenities.toList()) }
                                .onFailure { failed += "amenities" }
                        }
                        creating = false
                        if (failed.isEmpty()) {
                            onCreated()
                        } else {
                            createdWithWarnings = true
                            error = "Клуб создан, но не удалось сохранить: ${failed.joinToString()}. " +
                                "Откройте страницу клуба, чтобы завершить настройку."
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FormSectionTitle(text: String) {
    Box(Modifier.height(20.px))
    SpanText(
        text,
        Modifier.bodyMMedium().color(AppColors.Ink)
            .styleModifier { property("font-weight", "700") }
            .margin(bottom = 10.px),
    )
}

@Composable
private fun CreateClubButton(onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 18.px, topBottom = 9.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", AppColors.Ink.toRgbString())
                property("transition", "background 140ms ease")
            }
            .onClick { onClick() },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            LucideIcon(name = "plus", size = 15, color = AppColors.Surface.toRgbString())
            Box(Modifier.width(7.px))
            SpanText(
                "Создать клуб",
                Modifier.bodySMedium().color(AppColors.Surface)
                    .styleModifier { property("font-weight", "600") },
            )
        }
    }
}

private fun Double.asFixed(digits: Int): String = this.asDynamic().toFixed(digits) as String
